import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HubLovService } from '../../../../../core/services/hub-lov.service';
import { BusinessUnitModel, FinanceProjectModel, FinanceProjectSearchModel, FinanceProjectSearchResultModel, LinkedFinanceProjectModel, ProjectFinanceProjectModel, ProjectModel, SortModel } from '../../../../../../hub_schema/hubTypes';
import { MatLabel, MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { Subscription, take } from 'rxjs';
import { SharedModule } from '../../../../../shared/shared.module';
import { MatDialogRef } from '@angular/material/dialog';
import { HubRecordEditorBase } from '../../../_hub_record_editor_base';
import { UserDataService } from '../../../../../core/services/user-data.service';

@Component({
    selector: 'app-link-workday-projects-dialog',
    imports: [MatSelectModule, MatLabel, ReactiveFormsModule, CommonModule, MatInputModule, SharedModule],
    templateUrl: './link-workday-projects-dialog.component.html',
    styleUrl: './link-workday-projects-dialog.component.scss'
})
export class LinkWorkdayProjectsDialogComponent extends HubRecordEditorBase implements OnInit, OnDestroy {
    private hubLovService: HubLovService = inject(HubLovService);
    private userDataService: UserDataService = inject(UserDataService);
    private dialogRef: MatDialogRef<LinkWorkdayProjectsDialogComponent> = inject(MatDialogRef<LinkWorkdayProjectsDialogComponent>);

    public businessUnits!: BusinessUnitModel[];
    public filterFormChangedSubscription: Subscription;
    public searchResult: FinanceProjectSearchResultModel;

    private sortState: SortModel = {
        active: 'startDate',
        direction: 'desc'
    };

    public dateRanges = [
        { days: 30, name: "Past 30 Days" },
        { days: 60, name: "Past 60 Days" },
        { days: 90, name: "Past 90 Days" },
        { days: -1, name: "All Time" }
    ];

    public filterForm: FormGroup = new FormGroup({
        dateRangeFilter: new FormControl(this.dateRanges[3]),
        buFilters: new FormControl([]),
        workdayNameFilter: new FormControl('')
    });

    public currentPage: number = 0;

    public ngOnInit(): void {
        this.hubLovService.getBusinessUnits()
            .subscribe((businessUnits) => {
                const noBu: any = {
                    businessUnitId: -1,
                    name: '(No BU in Workday)'
                };
                this.businessUnits = [noBu, ...businessUnits];
            });

        this.populateWorkdayProjects();

        this.filterFormChangedSubscription = this.filterForm.valueChanges.subscribe((formValue) => {
            this.currentPage = 0;
            this.populateWorkdayProjects();
        });
    }

    private populateWorkdayProjects(): void {
        const filterFormValue = this.filterForm.value;

        const searchModel: FinanceProjectSearchModel = {
            days: filterFormValue.dateRangeFilter?.days || null,
            workdayName: filterFormValue.workdayNameFilter,
            businessUnitIds: filterFormValue.buFilters.map((bu: BusinessUnitModel) => bu.businessUnitId) || [],
            sort: this.sortState,
            page: this.currentPage
        };

        this.hubLovService.searchFinanceProjects(searchModel).pipe(take(1)).subscribe((result) => {
            this.searchResult = result;
        });
    }

    public pageRequested(page): void {
        this.currentPage = page;
        this.populateWorkdayProjects();
    }

    public resetFilters(): void {
        this.filterForm.reset({
            dateRangeFilter: this.dateRanges[3],
            buFilters: [],
            workdayProjectFilter: '',
        });
        this.currentPage = 0;
    }

    public sortData(sortState: SortModel): void {
        this.sortState = sortState;
        this.currentPage = 0;
        this.populateWorkdayProjects();
    }

    public isLinked(financeProject: LinkedFinanceProjectModel): boolean {
        return (!!financeProject.linkedProjectId && financeProject.linkedProjectId !== this.project.projectId)
            || this.isLinkedToThisProject(financeProject);
    }

    public isLinkedToThisProject(financeProject: LinkedFinanceProjectModel): boolean {
        return (this.projectEditForm?.controls.projectFinanceProjects.value as ProjectFinanceProjectModel[])
            .some(lfp => lfp.financeProject!.id === financeProject.id);
    }

    public addLink(financeProject: LinkedFinanceProjectModel) {
        const pfp: ProjectFinanceProjectModel = {
            projectId: this.project.projectId!,
            financeProjectId: financeProject.id,
            isActive: true,
            isArchived: false,
            createdByUser: {
                userId: this.userDataService.getUserId(),
                email: this.userDataService.getUserEmail(),
                lastName: this.userDataService.getUserLastName(),
                fullName: this.userDataService.getUserFullName()
            } as any,
            createdOn: new Date()
        };
        pfp.financeProject = financeProject;
        financeProject.linkedProjectId = this.project.projectId;
        this.projectEditForm?.controls.projectFinanceProjects.value.push(pfp);
        this.projectEditForm?.controls.projectFinanceProjects.markAsDirty();
        this.projectEditForm!.controls.projectFinanceProjects.updateValueAndValidity();
    }

    public close(): void {
        // return back the things that have changed.
        this.dialogRef.close();
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public ngOnDestroy(): void {
        if (this.filterFormChangedSubscription) {
            this.filterFormChangedSubscription.unsubscribe();
        }
    }

}
